import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
	Box,
	Checkbox,
	TextField,
	List,
	ListItem,
	ListItemText,
	IconButton,
} from "@material-ui/core";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import DeleteIcon from "@material-ui/icons/Delete";
import EquipmentUsageFields from "features/equipment/components/equipment-usage-fields";
import { supportsUsageRecording } from "features/equipment/utils/equipment-usage-math";

const useStyles = makeStyles((theme) => ({
	selectRow: {
		borderBottom: "1px solid #eeeeee",
		width: "100%",
	},
	selectedList: {
		marginTop: theme.spacing(1),
		padding: 0,
	},
	notesField: {
		marginLeft: theme.spacing(1),
		flex: 1,
	},
	listItem: {
		paddingLeft: 0,
		paddingRight: 0,
		alignItems: "flex-start",
		flexDirection: "column",
	},
	itemHeader: {
		display: "flex",
		alignItems: "center",
		width: "100%",
	},
	usageFields: {
		width: "100%",
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
	},
}));

const emptyUsage = (opt) => ({
	organisationEquipmentId: opt?.value,
	equipmentName: opt?.label || "",
	notes: "",
	captureUsage: false,
	startReading: opt?.currentHoursKms ?? null,
	endReading: null,
	unitsUsed: null,
	updateCurrentReading: false,
});

const EquipmentMultiSelect = ({
	options = [],
	value = [],
	onChange,
	disabled,
	captureUsage = false,
	selectLabel = "Select Equipment",
}) => {
	const classes = useStyles();
	const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
	const checkedIcon = <CheckBoxIcon fontSize="small" />;

	const selectableOptions = captureUsage
		? options.filter((o) => supportsUsageRecording(o.maintenanceType))
		: options;

	const selectedIds = value.map((v) => v.organisationEquipmentId);
	const autocompleteValue = selectableOptions.filter((o) =>
		selectedIds.includes(o.value)
	);

	const onSelect = useCallback(
		(_, selected) => {
			const newIds = selected.map((s) => s.value);
			const updated = newIds.map((id) => {
				const existing = value.find((v) => v.organisationEquipmentId === id);
				if (existing) {
					return { ...existing, captureUsage };
				}
				const opt = options.find((o) => o.value === id);
				return {
					...emptyUsage(opt),
					captureUsage,
				};
			});
			onChange(updated);
		},
		[value, options, onChange, captureUsage]
	);

	const onItemChange = useCallback(
		(equipmentId, partial) => {
			const updated = value.map((v) =>
				v.organisationEquipmentId === equipmentId ? { ...v, ...partial } : v
			);
			onChange(updated);
		},
		[value, onChange]
	);

	const onRemove = useCallback(
		(equipmentId) => {
			onChange(value.filter((v) => v.organisationEquipmentId !== equipmentId));
		},
		[value, onChange]
	);

	return (
		<>
			<Autocomplete
				multiple
				id="equipment-multiselect"
				options={selectableOptions}
				disableCloseOnSelect
				getOptionLabel={(option) => option.label}
				disabled={disabled}
				onChange={onSelect}
				value={autocompleteValue}
				renderOption={(option, { selected }) => (
					<div className={classes.selectRow}>
						<Checkbox
							icon={icon}
							checkedIcon={checkedIcon}
							style={{ marginRight: 8 }}
							checked={selectedIds.includes(option.value)}
							disabled={disabled}
						/>
						{option.label}
					</div>
				)}
				renderInput={(params) => (
					<TextField {...params} variant="outlined" label={selectLabel} />
				)}
			/>
			{value.length > 0 && (
				<List className={classes.selectedList} dense>
					{value.map((item) => {
						const opt = options.find((o) => o.value === item.organisationEquipmentId);
						return (
							<ListItem key={item.organisationEquipmentId} className={classes.listItem}>
								<Box className={classes.itemHeader}>
									<ListItemText
										primary={item.equipmentName || opt?.label || ""}
										style={{ flex: "0 0 auto", minWidth: 120 }}
									/>
									<TextField
										className={classes.notesField}
										size="small"
										variant="outlined"
										placeholder="Notes"
										value={item.notes || ""}
										onChange={(e) =>
											onItemChange(item.organisationEquipmentId, {
												notes: e.target.value,
											})
										}
										disabled={disabled}
									/>
									<IconButton
										size="small"
										onClick={() => onRemove(item.organisationEquipmentId)}
										disabled={disabled}
									>
										<DeleteIcon fontSize="small" />
									</IconButton>
								</Box>
								{captureUsage && (
									<Box className={classes.usageFields}>
										<EquipmentUsageFields
											unitType={opt?.maintenanceType}
											unitsUsed={item.unitsUsed}
											startReading={item.startReading}
											endReading={item.endReading}
											notes={item.notes}
											updateCurrentReading={item.updateCurrentReading}
											showNotes={false}
											disabled={disabled}
											onChange={(next) =>
												onItemChange(item.organisationEquipmentId, {
													...next,
													captureUsage: true,
												})
											}
										/>
									</Box>
								)}
							</ListItem>
						);
					})}
				</List>
			)}
		</>
	);
};

EquipmentMultiSelect.propTypes = {
	options: PropTypes.array,
	value: PropTypes.array,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	captureUsage: PropTypes.bool,
	selectLabel: PropTypes.string,
};

export default EquipmentMultiSelect;
